<template src="./omr-quiz-results-dashboard.html"></template>

<script>
import appHeader from "../../../../shared-module/components/app-header/app-header.vue";
import omrService from "../../../services/omr-service";

export default {
  name: "app-omr-quiz-results-dashboard",
  components: {
    "app-header": appHeader,
  },
  data() {
    return {
      quizId: "",
      loading: true,
      error: "",
      dashboard: null,
      searchQuery: "",
      expandedKeys: {},
      exporting: false,
      searchDebounce: null,
    };
  },
  computed: {
    summary() {
      return (this.dashboard && this.dashboard.summary) || {};
    },
    students() {
      return (this.dashboard && this.dashboard.students) || [];
    },
    filteredStudents() {
      const q = String(this.searchQuery || "").trim().toLowerCase();
      if (!q) return this.students;
      return this.students.filter((row) => {
        const name = String(row.user?.full_name || "").toLowerCase();
        const email = String(row.user?.email || "").toLowerCase();
        const phone = String(row.user?.phone || "").toLowerCase();
        const id = String(row.user?.id || row.user_id || "");
        return name.includes(q) || email.includes(q) || phone.includes(q) || id.includes(q);
      });
    },
  },
  mounted() {
    if (this.$route.query.quizId) {
      this.quizId = this.$route.query.quizId;
      this.loadDashboard();
    } else {
      this.loading = false;
      this.error = "Quiz id is missing. Open results from Quiz Center.";
    }
  },
  beforeDestroy() {
    if (this.searchDebounce) clearTimeout(this.searchDebounce);
  },
  methods: {
    async loadDashboard() {
      this.loading = true;
      this.error = "";
      try {
        const response = await omrService.getResultsDashboard(this.quizId);
        if (response.data.status === "success") {
          this.dashboard = response.data.data;
        } else {
          this.error = response.data.message || "Failed to load OMR results";
        }
      } catch (err) {
        const msg = err.response?.data?.message || err.message || "Failed to load OMR results";
        this.error = msg;
        this.$toast.error(msg);
      } finally {
        this.loading = false;
      }
    },
    onSearchInput() {
      if (this.searchDebounce) clearTimeout(this.searchDebounce);
      this.searchDebounce = setTimeout(() => {}, 150);
    },
    clearSearch() {
      this.searchQuery = "";
    },
    rowKey(row) {
      return row.user_id != null ? String(row.user_id) : `row-${row.attempts?.[0]?.result_id || "x"}`;
    },
    isExpanded(key) {
      return !!this.expandedKeys[key];
    },
    toggleExpand(key) {
      this.$set(this.expandedKeys, key, !this.expandedKeys[key]);
    },
    openDetail(resultId) {
      if (!resultId) return;
      this.$router.push(`/quiz/individual-results-omr?resultId=${resultId}`);
    },
    formatNum(n) {
      const v = Number(n);
      if (!Number.isFinite(v)) return "0.0";
      return v.toFixed(1);
    },
    formatDate(iso) {
      if (!iso) return "—";
      try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return "—";
        return d.toLocaleString(undefined, {
          year: "numeric",
          month: "short",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        });
      } catch (e) {
        return "—";
      }
    },
    accuracyClass(pct) {
      const n = Number(pct);
      if (n >= 70) return "acc-high";
      if (n >= 50) return "acc-mid";
      return "acc-low";
    },
    exportCsv() {
      if (!this.filteredStudents.length) return;
      this.exporting = true;
      try {
        const rows = [];
        const headers = [
          "Student ID",
          "Student Name",
          "Email",
          "Phone",
          "Attempt #",
          "Date",
          "Correct",
          "Wrong",
          "Unmarked",
          "Total Questions",
          "Obtained Marks",
          "Accuracy %",
          "Pass/Fail",
          "Has Scan",
        ];
        rows.push(headers);

        this.filteredStudents.forEach((student) => {
          (student.attempts || []).forEach((att) => {
            rows.push([
              student.user?.id || student.user_id || "",
              this.csvEscape(student.user?.full_name || ""),
              this.csvEscape(student.user?.email || ""),
              student.user?.phone || "",
              att.attempt_number,
              att.created_at || "",
              att.correct,
              att.incorrect,
              att.unmarked,
              att.total,
              att.obtained_marks,
              att.accuracy,
              att.passed ? "pass" : "fail",
              att.has_scan ? "yes" : "no",
            ]);
          });
        });

        const csv = rows.map((r) => r.join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        const safeName = (this.dashboard?.quiz?.name || "omr-results").replace(/[^a-z0-9-_]+/gi, "-");
        link.href = url;
        link.download = `${safeName}-submissions.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        this.$toast.success("CSV exported");
      } catch (e) {
        this.$toast.error("Export failed");
      } finally {
        this.exporting = false;
      }
    },
    csvEscape(str) {
      const s = String(str || "");
      if (s.includes(",") || s.includes('"') || s.includes("\n")) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    },
  },
};
</script>

<style src="./omr-quiz-results-dashboard.css" scoped></style>
